import { v4 as uuid } from 'uuid';
import { TextElementInput, Font } from '../types';
import { getCaptionHighlightColor } from './aiEditsProcessor';
import { VideoElement } from '@/App/remotion/MainPlayer/types/Element/VideoElement';
import { AudioElement } from '@/App/remotion/MainPlayer/types/Element/AudioElement';
import { ImageElement } from '@/App/remotion/MainPlayer/types/Element/ImageElement';
import { TextElement } from '@/App/remotion/MainPlayer/types/Element/TextElement';
import { ElementType } from '@/App/remotion/MainPlayer/types/Element/Type';
import { Clip, SizeType } from '@/domains/asset';
import { getAlignedRecordings } from '@/App/remotion/MainPlayer/stores/alignedRecordings';
import { TransitionInEffect } from '@/App/remotion/libs/transitions/types';
import { buildCalloutStyles } from '@/Pages/Clip/ClipPlayer/ClipAssets/CalloutCaptions/calloutDefaults';
import { calculateContainerWidth } from '@/Pages/Clip/ClipPlayer/ClipAssets/CalloutCaptions/textUtils';
import { getClipBroadcastId, isClipFromEvents, isClipFromUploads, isClipPreRecordedEvent } from '@/libs/clipUtils';
import { getSpeakerDetailsForBroadcast } from '@/App/remotion/MainPlayer/stores/speakerDetailsStore';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';
import { getAudioDisplayName } from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/audioOverlayLibraryUtils';
import { getAudioDurationFromUrl } from '@/libs/getAudioDuration';
import { doesClipWorkOnAlignedStreams } from '@/stores/clip';
import { Scene } from '@/App/remotion/MainPlayer/types/Scene';
import { getSegmentTimeFromPlayerTime } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const sanitizeVideoElements = (videos: any[], fps: number): VideoElement[] => {
  return videos.map((item, index) => ({
    id: item.id || uuid(),
    name: capitalizeFirstLetter(item.b_roll_title) || `Video ${index + 1}`,
    trim: [],
    type: ElementType.VIDEO,
    details: {
      src: item.url,
      altText: `Video ${index + 1}`,
      volume: [
        {
          value: 20,
          // was 24 used as proxy for FPS?
          toFrame: fps * (item.end - item.start),
          fromFrame: 0
        }
      ],
      duration: item.end - item.start
    },
    containerStyle: {
      position: 'absolute',
      top: '0%',
      left: '0%',
      height: '100%',
      width: '100%'
    },
    style: {
      height: '100%',
      width: '100%',
      objectFit: 'cover',
      borderRadius: '0px',
      ...(item.style || {})
    },
    toFrame: item.end * fps,
    fromFrame: (item.start < 0 ? 0 : item.start) * fps,
    effects: item.effects || [],
    isOriginalVideo: false,
    metadata: item.metadata
  }));
};

const AUDIO_DURATION_TIMEOUT_MS = 5000;

const getAudioDurationWithTimeout = async (url: string): Promise<number | undefined> => {
  try {
    return await Promise.race([
      getAudioDurationFromUrl(url),
      new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), AUDIO_DURATION_TIMEOUT_MS))
    ]);
  } catch {
    return undefined;
  }
};

export const sanitizeAudioElements = async (
  audios: any[],
  fps: number,
  compositeOffsetFrames: number = 0
): Promise<AudioElement[]> => {
  const audioDurations = await Promise.all(audios.map(item => getAudioDurationWithTimeout(item.url)));

  return audios.map((item, index) => ({
    id: item.id || uuid(),
    name: getAudioDisplayName({ url: item.url, name: item.audio_title }),
    type: ElementType.AUDIO,
    details: {
      src: item.url,
      duration: audioDurations[index],
      volume: [
        {
          value: 15,
          // was 24 used as proxy for FPS?
          toFrame: fps * (item.end - item.start),
          fromFrame: 0
        }
      ],
      fadeIn: 0,
      fadeOut: 0,
      loop: true
    },
    trim: [],
    toFrame: item.end * fps + compositeOffsetFrames,
    fromFrame: item.start * fps + compositeOffsetFrames,
    metadata: item.metadata
  }));
};

export const sanitizeImageElements = (images: any[], fps: number): ImageElement[] => {
  return images.map((item, index) => ({
    id: item.id || uuid(),
    name: `Image ${index + 1}`,
    type: ElementType.IMAGE,
    details: {
      src: item.url,
      altText: `Image ${index + 1}`
    },
    containerStyle: {},
    style: {
      height: '100%',
      objectFit: 'cover',
      ...(item.style || {})
    },
    fromFrame: item.start * fps,
    toFrame: item.end * fps,
    effects: item.effects || [],
    metadata: item.metadata
  }));
};

export const sanitizeCallOutElements = (
  font: Font,
  textElements: TextElementInput[],
  fps: number,
  videoLayout: SizeType
): TextElement[] => {
  const backgroundColor = getCaptionHighlightColor();

  return textElements.map((textElement, index) => {
    const fromFrame = Math.ceil(textElement.start * fps);
    const toFrame = Math.ceil((textElement.start + textElement.duration) * fps);

    const containerWidthPx = calculateContainerWidth(videoLayout);

    const { containerStyle, style } = buildCalloutStyles(
      textElement.text,
      backgroundColor,
      font.name,
      'AI-EDITS',
      containerWidthPx,
      videoLayout
    );

    return {
      id: textElement.id || uuid(),
      name: textElement.text || `Call-out Text ${index + 1}`,
      type: ElementType.TEXT,
      fromFrame,
      toFrame,
      details: {
        text: textElement.text,
        font,
        areCaptionsHidden: true
      },
      containerStyle,
      style: {
        ...style,
        WebkitTextStrokeWidth: undefined,
        WebkitTextStrokeColor: undefined,
        paintOrder: 'normal' as const
      },
      effects: [],
      transitions: { wordIn: TransitionInEffect.SlideUp },
      metadata: textElement.metadata
    };
  });
};

export const sanitizeScenes = (scenes: any, fps: number, clipData: Clip) => {
  let allVideoIds = getAlignedRecordings(getClipBroadcastId(clipData)).map(recording => recording.id);

  let faceIdToSpeakerKey: Record<number, string> = {};
  if (!doesClipWorkOnAlignedStreams(clipData)) {
    const speakerDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clipData)!);
    allVideoIds = Object.keys(speakerDetails);

    const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clipData.id];
    const speakerMapping = speakersAnalysis.speaker_mapping;

    const faceIdToSpeakerId = Object.entries(speakerMapping).reduce(
      (acc, [speakerId, faceIds]) => {
        acc[faceIds[0]] = speakerId;
        return acc;
      },
      {} as Record<number, string>
    );

    const speakerIdToSpeakerKey = Object.entries(speakerDetails).reduce(
      (acc, [_, speaker]) => {
        acc[speaker.id] = speaker.key;
        return acc;
      },
      {} as Record<string, string>
    );

    faceIdToSpeakerKey = Object.entries(faceIdToSpeakerId).reduce(
      (acc, [faceId, speakerId]) => {
        acc[faceId] = speakerIdToSpeakerKey[speakerId];
        return acc;
      },
      {} as Record<number, string>
    );
  }

  const deletes = clipData.asset_metadata.deletes || {};

  // Convert deletes to sorted array for easier processing
  const deletesArray = Object.values(deletes)
    .sort((a, b) => a.bounds[0] - b.bounds[0])
    .filter(deleteItem => deleteItem.bounds[0] !== deleteItem.bounds[1]); // Filter out invalid deletes
  // Helper function to map playable time back to original timeline
  const mapPlayableToOriginal = (playableTime: number): number => {
    let currentPlayableTime = 0;
    let currentOriginalTime = 0;

    // Walk through original timeline, building playable segments
    for (const deleteItem of deletesArray) {
      const [deleteStart, deleteEnd] = deleteItem.bounds;

      // Duration of available content before this delete
      const availableDuration = deleteStart - currentOriginalTime;

      // If our target playable time falls within this available segment
      if (currentPlayableTime + availableDuration >= playableTime) {
        const offsetInSegment = playableTime - currentPlayableTime;
        return currentOriginalTime + offsetInSegment;
      }

      // Move past this available segment
      currentPlayableTime += availableDuration;
      currentOriginalTime = deleteEnd; // Skip the deleted portion
    }

    const remainingTime = playableTime - currentPlayableTime;
    return currentOriginalTime + remainingTime;
  };

  // Map scenes from playable timeline to original timeline
  const adjustedScenes = scenes.map((scene, index) => {
    // Input scene times are in playable timeline
    const playableStart = scene.start;
    const playableEnd = scene.end;

    // Map to original timeline
    const originalStart = mapPlayableToOriginal(playableStart);
    const originalEnd = mapPlayableToOriginal(playableEnd);

    const customUploadIds =
      scene.speakers.length > 0 && Object.keys(faceIdToSpeakerKey).length > 0
        ? scene.speakers.map(speaker => faceIdToSpeakerKey[speaker])
        : undefined;

    let visibleVideoIds = customUploadIds ? customUploadIds : scene.speakers.length > 0 ? scene.speakers : allVideoIds;

    if (isClipPreRecordedEvent(clipData) || isClipFromUploads(clipData) || isClipFromEvents(clipData)) {
      if (!allVideoIds.includes('original-video')) {
        allVideoIds = allVideoIds.concat('original-video');
      }
      if (!visibleVideoIds.includes('original-video')) {
        visibleVideoIds = visibleVideoIds.concat('original-video');
      }
    }
    return {
      id: scene.id || crypto.randomUUID(),
      name: `Scene ${index + 1}`,
      details: {
        layout: scene.speakers?.length > 0 ? scene.layout.toUpperCase() : 'DEFAULT',
        fitMode: 'FILL',
        all_video_ids: allVideoIds,
        visible_video_ids: visibleVideoIds
      },
      fromFrame: Math.max(0, originalStart * fps),
      toFrame: Math.max(0, originalEnd * fps),
      originalIndex: index,
      metadata: scene.metadata
    };
  });

  const targetEndFrame = clipData.asset_metadata.end * fps;

  // Sort scenes by start frame for gap analysis
  const sortedScenes = [...adjustedScenes].sort((a, b) => a.fromFrame - b.fromFrame);

  // Ensure first scene starts at 0
  if (sortedScenes.length > 0 && sortedScenes[0].fromFrame > clipData.asset_metadata.start * fps) {
    sortedScenes[0].fromFrame = clipData.asset_metadata.start * fps;
  }

  // Ensure last scene covers to the end
  if (sortedScenes.length > 0 && sortedScenes[sortedScenes.length - 1].toFrame < targetEndFrame) {
    sortedScenes[sortedScenes.length - 1].toFrame = targetEndFrame;
  }

  // Fill gaps between scenes
  for (let i = 1; i < sortedScenes.length; i++) {
    const prevScene = sortedScenes[i - 1];
    const currentScene = sortedScenes[i];

    if (prevScene.toFrame < currentScene.fromFrame) {
      // Extend previous scene to fill the gap
      prevScene.toFrame = currentScene.fromFrame;
    } else if (prevScene.toFrame > currentScene.fromFrame) {
      // Adjust current scene to avoid overlap
      currentScene.fromFrame = prevScene.toFrame;
    }
  }

  // Sort back to original order
  return sortedScenes.sort((a, b) => a.originalIndex - b.originalIndex).map(({ originalIndex, ...scene }) => scene);
};

export const sanitizeEffects = (effects: any[], fps: number, clipData: Clip) => {
  if (!effects || effects.length === 0) {
    return [];
  }

  const deletes = clipData.asset_metadata.deletes || {};

  // Convert deletes to sorted array for easier processing
  const deletesArray = Object.values(deletes)
    .sort((a, b) => a.bounds[0] - b.bounds[0])
    .filter(deleteItem => deleteItem.bounds[0] !== deleteItem.bounds[1]); // Filter out invalid deletes

  // Helper function to map playable time back to original timeline
  const mapPlayableToOriginal = (playableTime: number): number => {
    let currentPlayableTime = 0;
    let currentOriginalTime = 0;

    // Walk through original timeline, building playable segments
    for (const deleteItem of deletesArray) {
      const [deleteStart, deleteEnd] = deleteItem.bounds;

      // Duration of available content before this delete
      const availableDuration = deleteStart - currentOriginalTime;

      // If our target playable time falls within this available segment
      if (currentPlayableTime + availableDuration > playableTime) {
        const offsetInSegment = playableTime - currentPlayableTime;
        return currentOriginalTime + offsetInSegment;
      }
      // Move past this available segment
      currentPlayableTime += availableDuration;
      currentOriginalTime = deleteEnd; // Skip the deleted portion
    }
    const remainingTime = playableTime - currentPlayableTime;
    return currentOriginalTime + remainingTime;
  };

  // Map effects from playable timeline to original timeline
  return effects.map((effect, index) => {
    // Input effect times are in playable timeline
    const playableStart = effect.start;
    const playableEnd = playableStart + effect.duration;

    // Map to original timeline
    const originalStart = mapPlayableToOriginal(playableStart);
    const originalEnd = mapPlayableToOriginal(playableEnd);

    return {
      ...effect,
      id: effect.id || `effect-${index}`,
      start: originalStart,
      duration: originalEnd - originalStart,
      metadata: effect.metadata
    };
  });
};

export const sanitizeTrims = (trims: any[], clipData: Clip) => {
  if (!trims || trims.length === 0) {
    return [];
  }

  const deletes = clipData.asset_metadata.deletes || {};

  const deletesArray = Object.values(deletes)
    .sort((a, b) => a.bounds[0] - b.bounds[0])
    .filter(deleteItem => deleteItem.bounds[0] !== deleteItem.bounds[1]);

  const introDuration = clipData.asset_metadata.introDuration || 0;

  const mapPlayableToOriginal = (playableTime: number): number => {
    let currentPlayableTime = 0;
    let currentOriginalTime = 0;

    for (const deleteItem of deletesArray) {
      const [deleteStart, deleteEnd] = deleteItem.bounds;
      const availableDuration = deleteStart - currentOriginalTime;

      if (currentPlayableTime + availableDuration >= playableTime) {
        const offsetInSegment = playableTime - currentPlayableTime;
        return currentOriginalTime + offsetInSegment;
      }

      currentPlayableTime += availableDuration;
      currentOriginalTime = deleteEnd;
    }

    const remainingTime = playableTime - currentPlayableTime;
    return currentOriginalTime + remainingTime;
  };

  return trims
    .filter(trim => {
      const [start, end] = trim;
      return start < end;
    })
    .map(trim => {
      const [playableStart, playableEnd] = trim;

      const adjustedStart = Math.max(0, playableStart - introDuration);
      const adjustedEnd = Math.max(0, playableEnd - introDuration);

      const originalStart = mapPlayableToOriginal(adjustedStart);
      const originalEnd = mapPlayableToOriginal(adjustedEnd);

      return {
        bounds: [originalStart, originalEnd] as [number, number],
        prev_end_time: originalStart,
        next_start_time: originalEnd
      };
    });
};

/**
 * Helper: Extracts video IDs and speaker mapping for a clip.
 */
export const getVideoIdsAndSpeakerMapping = (clipData: Clip) => {
  let allVideoIds = getAlignedRecordings(getClipBroadcastId(clipData)).map(recording => recording.id);

  let faceIdToSpeakerKey: Record<number, string> = {};

  if (!doesClipWorkOnAlignedStreams(clipData)) {
    const speakerDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clipData)!);
    allVideoIds = Object.keys(speakerDetails);

    const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clipData.id];
    const speakerMapping = speakersAnalysis.speaker_mapping;

    const faceIdToSpeakerId = Object.entries(speakerMapping).reduce(
      (acc, [speakerId, faceIds]) => {
        acc[faceIds[0]] = speakerId;
        return acc;
      },
      {} as Record<number, string>
    );

    const speakerIdToSpeakerKey = Object.entries(speakerDetails).reduce(
      (acc, [_, speaker]) => {
        acc[speaker.id] = speaker.key;
        return acc;
      },
      {} as Record<string, string>
    );

    faceIdToSpeakerKey = Object.entries(faceIdToSpeakerId).reduce(
      (acc, [faceId, speakerId]) => {
        acc[faceId] = speakerIdToSpeakerKey[speakerId];
        return acc;
      },
      {} as Record<number, string>
    );
  }

  // Add original-video if needed
  if (isClipPreRecordedEvent(clipData) || isClipFromUploads(clipData) || isClipFromEvents(clipData)) {
    if (!allVideoIds.includes('original-video')) {
      allVideoIds = allVideoIds.concat('original-video');
    }
  }

  return { allVideoIds, faceIdToSpeakerKey };
};

/**
 * Per-split sanitizer for scenes: maps zero-based split times to absolute timeline
 * using the per-split deletes and segment start offset.
 */
export const sanitizeScenesForSplit = (
  layouts: any,
  fps: number,
  compositeSegment: CompositeSegment,
  clipData: Clip
): Scene[] => {
  const { allVideoIds, faceIdToSpeakerKey } = getVideoIdsAndSpeakerMapping(clipData);

  const adjustedScenes = layouts.map((scene, index) => {
    // Times from backend are zero-based within the split
    const playableStart = scene.start + compositeSegment.fromFrame / fps;
    const playableEnd = scene.end + compositeSegment.fromFrame / fps;

    // Map through per-split deletes
    const playableStartInOriginal = getSegmentTimeFromPlayerTime(playableStart, clipData, compositeSegment.id);
    const playableEndInOriginal = getSegmentTimeFromPlayerTime(playableEnd, clipData, compositeSegment.id);

    const customUploadIds =
      scene.speakers.length > 0 && Object.keys(faceIdToSpeakerKey).length > 0
        ? scene.speakers.map(speaker => faceIdToSpeakerKey[speaker])
        : undefined;

    let visibleVideoIds = customUploadIds ? customUploadIds : scene.speakers.length > 0 ? scene.speakers : allVideoIds;

    if (!visibleVideoIds.includes('original-video') && allVideoIds.includes('original-video')) {
      visibleVideoIds = visibleVideoIds.concat('original-video');
    }

    return {
      id: scene.id || crypto.randomUUID(),
      name: `Scene ${index + 1}`,
      details: {
        layout: scene.speakers?.length > 0 ? scene.layout.toUpperCase() : 'DEFAULT',
        fitMode: 'FILL',
        all_video_ids: allVideoIds,
        visible_video_ids: visibleVideoIds
      },
      fromFrame: Math.max(0, playableStartInOriginal * fps),
      toFrame: Math.max(0, playableEndInOriginal * fps),
      originalIndex: index,
      metadata: scene.metadata
    };
  });

  const segmentStartFrame = compositeSegment.currentBounds.fromFrame;
  const segmentEndFrame = compositeSegment.currentBounds?.toFrame;

  // Sort scenes by start frame for gap analysis
  const sortedScenes = [...adjustedScenes].sort((a, b) => a.fromFrame - b.fromFrame);

  // Ensure first scene starts at segment start
  if (sortedScenes.length > 0 && sortedScenes[0].fromFrame > segmentStartFrame) {
    sortedScenes[0].fromFrame = segmentStartFrame;
  }

  // Ensure last scene covers to segment end
  if (sortedScenes.length > 0 && sortedScenes[sortedScenes.length - 1].toFrame < segmentEndFrame) {
    sortedScenes[sortedScenes.length - 1].toFrame = segmentEndFrame;
  }

  // Fill gaps between scenes
  for (let i = 1; i < sortedScenes.length; i++) {
    const prevScene = sortedScenes[i - 1];
    const currentScene = sortedScenes[i];

    if (prevScene.toFrame < currentScene.fromFrame) {
      // Extend previous scene to fill the gap
      prevScene.toFrame = currentScene.fromFrame;
    } else if (prevScene.toFrame > currentScene.fromFrame) {
      // Adjust current scene to avoid overlap
      currentScene.fromFrame = prevScene.toFrame;
    }
  }

  return sortedScenes;
};

/**
 * Per-split sanitizer for effects: maps zero-based split times to absolute timeline
 * using the per-split deletes and segment start offset.
 */
export const sanitizeEffectsForSplit = (
  effects: any[],
  fps: number,
  compositeSegment: CompositeSegment,
  clipData: Clip
) => {
  if (!effects || effects.length === 0) {
    return [];
  }

  return effects.map((effect, index) => {
    // Times from backend are zero-based within the split
    const playableStart = effect.start + compositeSegment.fromFrame / fps;
    const playableEnd = playableStart + effect.duration;

    // Map through per-split deletes
    const playableStartInOriginal = getSegmentTimeFromPlayerTime(playableStart, clipData, compositeSegment.id);
    const playableEndInOriginal = getSegmentTimeFromPlayerTime(playableEnd, clipData, compositeSegment.id);

    return {
      ...effect,
      id: effect.id || `effect-${index}`,
      start: playableStartInOriginal,
      duration: playableEndInOriginal - playableStartInOriginal,
      metadata: effect.metadata
    };
  });
};

/**
 * Per-split sanitizer for trims: maps zero-based split times to absolute timeline
 * using the per-split deletes and segment start offset.
 * Note: Backend already subtracted introDuration, so we don't repeat it.
 */
export const sanitizeTrimsForSplit = (trims: any[], fps: number, segmentMetadata: CompositeSegment, clipData: Clip) => {
  if (!trims || trims.length === 0) {
    return [];
  }

  return trims
    .filter(trim => {
      const [start, end] = trim;
      return start < end;
    })
    .map(trim => {
      let [playableStart, playableEnd] = trim;

      playableStart += segmentMetadata.fromFrame / fps;
      playableEnd += segmentMetadata.fromFrame / fps;

      // Backend already subtracted introDuration, so no need to do it again
      // Map through per-split deletes
      const playableStartInOriginal = getSegmentTimeFromPlayerTime(playableStart, clipData, segmentMetadata.id);
      const playableEndInOriginal = getSegmentTimeFromPlayerTime(playableEnd, clipData, segmentMetadata.id);

      return {
        bounds: [playableStartInOriginal, playableEndInOriginal] as [number, number],
        prev_end_time: playableStartInOriginal,
        next_start_time: playableEndInOriginal
      };
    });
};
